import { useCallback, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import type { AdminContact, EventAttendee, ScheduledEvent } from "@tih/shared";
import {
  ApiError,
  adminCheckInAttendee,
  adminCheckOutAttendee,
  adminCreateEventAttendee,
  adminFetchContactsBook,
  adminFetchEventAttendees,
  adminFetchEvents,
  adminFetchFeaturedEvent,
} from "../../api/client";
import {
  buildCheckInCandidates,
  inferRegisterPrefill,
  pickDefaultCheckInEvent,
  topUncheckedCandidate,
  type CheckInCandidate,
} from "../../lib/attendee-directory";
import { formatCheckInTime } from "../../lib/attendeeBulkParse";
import {
  eventDisplayTitle,
  formatEventWhen,
  phaseLabel,
  rowPhase,
} from "../../lib/event-admin-utils";

export function AdminCheckInPage() {
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [eventId, setEventId] = useState<number | null>(null);
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [contacts, setContacts] = useState<AdminContact[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [addName, setAddName] = useState("");
  const [addMobile, setAddMobile] = useState("");
  const [addLocation, setAddLocation] = useState("");
  const [addEmail, setAddEmail] = useState("");

  const selectedEvent = events.find((e) => e.id === eventId) ?? null;
  const now = Date.now();
  const hasSearch = Boolean(search.trim());

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, featured] = await Promise.all([
        adminFetchEvents(),
        adminFetchFeaturedEvent(),
      ]);
      setEvents(rows);
      setEventId((prev) => prev ?? pickDefaultCheckInEvent(rows, featured?.id ?? null));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load events");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLists = useCallback(async (opts?: { silent?: boolean }) => {
    if (events.length === 0) return;
    if (!opts?.silent) setListLoading(true);
    try {
      const [attendeeRows, book] = await Promise.all([
        eventId != null
          ? adminFetchEventAttendees(eventId)
          : Promise.resolve([] as EventAttendee[]),
        adminFetchContactsBook(),
      ]);
      setAttendees(attendeeRows);
      setContacts(book.contacts);
    } catch (e) {
      if (!opts?.silent) {
        setError(e instanceof ApiError ? e.message : "Could not load attendees");
      }
    } finally {
      if (!opts?.silent) setListLoading(false);
    }
  }, [events, eventId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const candidates = useMemo(
    () =>
      buildCheckInCandidates({
        attendees,
        contacts,
        selectedEventId: eventId,
        query: search,
      }),
    [attendees, contacts, eventId, search],
  );

  const tonight = attendees.filter((a) => a.eventId === eventId);
  const checkedIn = tonight.filter((a) => a.checkedInAt).length;
  function prefillRegister(from = search) {
    const inferred = inferRegisterPrefill(from);
    setAddName(inferred.name);
    setAddEmail(inferred.email);
    setAddMobile(inferred.mobile);
  }

  async function checkInCandidate(candidate: CheckInCandidate) {
    if (eventId == null) return;
    setBusyKey(candidate.key);
    setError(null);
    try {
      if (candidate.attendee && candidate.attendee.eventId === eventId) {
        await adminCheckInAttendee(candidate.attendee.id);
        setSuccess(`Checked in ${candidate.name}.`);
      } else {
        await adminCreateEventAttendee(eventId, {
          name: candidate.name,
          mobile: candidate.mobile,
          email: candidate.email,
          location: candidate.location,
          checkIn: true,
        });
        setSuccess(
          candidate.kind === "contact"
            ? `${candidate.name} checked in from the address book.`
            : `${candidate.name} checked in.`,
        );
      }
      setSearch("");
      await loadLists();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  }

  async function undoCheckIn(attendee: EventAttendee) {
    setBusyKey(`tonight:${attendee.id}`);
    setError(null);
    try {
      await adminCheckOutAttendee(attendee.id);
      setSuccess(`Undid check-in for ${attendee.name}.`);
      await loadLists();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  }

  async function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const top = topUncheckedCandidate(candidates);
    if (top) {
      await checkInCandidate(top);
      return;
    }
    if (search.trim()) prefillRegister();
  }

  async function submitRegister(e: FormEvent) {
    e.preventDefault();
    if (eventId == null || !addName.trim()) return;
    setError(null);
    try {
      await adminCreateEventAttendee(eventId, {
        name: addName.trim(),
        mobile: addMobile.trim() || null,
        location: addLocation.trim() || null,
        email: addEmail.trim() || null,
        checkIn: true,
      });
      setSuccess(`${addName.trim()} registered and checked in.`);
      setAddName("");
      setAddMobile("");
      setAddLocation("");
      setAddEmail("");
      setSearch("");
      await loadLists();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  if (loading) {
    return <div className="admin-page" style={{ opacity: 0.5 }}>Loading…</div>;
  }

  return (
    <div className="admin-page admin-checkin-page">
      <header className="admin-page-header">
        <h1>Check-in desk</h1>
        <p>
          Search tonight’s list and the address book by name, phone, or email, then check
          people into tonight’s event. Enter checks in the top unmatched result. Open self
          check-in on a tablet so visitors can enter their own details.
        </p>
      </header>

      {error && <div className="form-error admin-banner">{error}</div>}
      {success && (
        <div className="form-success admin-banner" onAnimationEnd={() => setSuccess(null)}>
          {success}
        </div>
      )}

      <div className="admin-card admin-checkin-toolbar">
        <label className="admin-field">
          <span>Event</span>
          <select
            value={eventId ?? ""}
            onChange={(e) => setEventId(Number(e.target.value) || null)}
          >
            {events.length === 0 && <option value="">No events</option>}
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {eventDisplayTitle(ev)} · {formatEventWhen(ev.startsAt)} (
                {phaseLabel(rowPhase(ev, now))})
              </option>
            ))}
          </select>
        </label>
        {selectedEvent && (
          <p className="admin-muted admin-checkin-stats">
            {checkedIn} checked in · {tonight.length} on tonight’s list · {contacts.length} in
            address book
            {" · "}
            <Link to="/admin/attendance">View attendance</Link>
          </p>
        )}
        <Link
          to={eventId != null ? `/admin/check-in/self?event=${eventId}` : "/admin/check-in/self"}
          className="btn btn-primary"
          target="_blank"
          rel="noreferrer"
        >
          Open self check-in
        </Link>
      </div>

      <div className="admin-card">
        <h3 className="admin-card-title">Register &amp; check in</h3>
        <p className="admin-card-sub">
          Use the form for a new face, or search first if they may already be on the list.
        </p>
        <form onSubmit={(e) => void submitRegister(e)}>
          <div className="admin-card-row">
            <label className="admin-field">
              <span>Name *</span>
              <input value={addName} onChange={(e) => setAddName(e.target.value)} required />
            </label>
            <label className="admin-field">
              <span>Mobile</span>
              <input value={addMobile} onChange={(e) => setAddMobile(e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Location</span>
              <input value={addLocation} onChange={(e) => setAddLocation(e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Email</span>
              <input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
              />
            </label>
          </div>
          <div className="admin-dashboard-links">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!addName.trim() || eventId == null}
            >
              Register &amp; check in
            </button>
          </div>
        </form>
      </div>

      <div className="admin-checkin-search-wrap" style={{ marginTop: 24 }}>
        <input
          type="search"
          className="admin-checkin-search"
          placeholder="Search name, phone, or email — Enter to check in"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => void onSearchKeyDown(e)}
          autoComplete="off"
          autoFocus
        />
      </div>

      {hasSearch && listLoading && <p>Loading attendees…</p>}

      {hasSearch && !listLoading && candidates.length > 0 && (
        <ul className="admin-checkin-list">
          {candidates.map((row) => {
            const kindLabel =
              row.kind === "tonight"
                ? "On tonight’s list"
                : "Address book";
            return (
              <li
                key={row.key}
                className={`admin-checkin-row${row.checkedInTonight ? " is-checked-in" : ""}`}
              >
                <div className="admin-checkin-row-main">
                  <strong>{row.name}</strong>
                  <span className="admin-checkin-row-meta">
                    {[row.mobile, row.email, row.location].filter(Boolean).join(" · ") || "—"}
                    {" · "}
                    {kindLabel}
                  </span>
                  {row.attendee?.checkedInAt && (
                    <span className="admin-checkin-time">
                      In at {formatCheckInTime(row.attendee.checkedInAt)}
                      {row.attendee.checkedInByName ? ` · ${row.attendee.checkedInByName}` : ""}
                    </span>
                  )}
                </div>
                <div className="admin-checkin-row-actions">
                  {row.checkedInTonight && row.attendee ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busyKey === row.key}
                      onClick={() => void undoCheckIn(row.attendee!)}
                    >
                      Undo
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busyKey === row.key || eventId == null}
                      onClick={() => void checkInCandidate(row)}
                    >
                      Check in
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {hasSearch && candidates.length === 0 && !listLoading && (
        <div className="admin-checkin-walkin">
          <p>
            No match for <strong>{search.trim()}</strong>.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => prefillRegister()}>
            Use in register form
          </button>
        </div>
      )}
    </div>
  );
}
